import { useSelector, type TypedUseSelectorHook } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { NavItem, StyledNav, StyledNavContainer } from "./index.styled";
import type { RootState } from "../../../store";

import settingIcon from "../../../assets/icons/setting.webp";
import settingActiveIcon from "../../../assets/icons/setting-active.webp";
import dashboardIcon from "../../../assets/icons/dashboard.webp";
import dashboardActiveIcon from "../../../assets/icons/dashboard-active.webp";
import contactIcon from "../../../assets/icons/contact.webp";
import contactActiveIcon from "../../../assets/icons/contact-active.webp";
import anonymousIcon from "../../../assets/icons/anonymous.webp";
import anonymousActiveIcon from "../../../assets/icons/anonymous-active.webp";

const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

function isNavItemActive(navPath: string, pathname: string): boolean {
  if (navPath === "/") {
    return pathname === "/" || pathname === "/dashboard";
  }
  if (navPath === "/anonymous") {
    return pathname === "/anonymous" || pathname.startsWith("/anonymous/");
  }
  return pathname === navPath;
}

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const statusIdFromUrl = useAppSelector(
    (state) => state.dashboard.statusIdFromUrl
  );

  const handleNavItemClick = (item: string) => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get("token");
    const shouldAttachToken = token && statusIdFromUrl === null;
    const newPath = shouldAttachToken ? `${item}?token=${token}` : item;
    navigate(newPath);
  };

  const iconMap = {
    setting: settingIcon,
    "setting-active": settingActiveIcon,
    dashboard: dashboardIcon,
    "dashboard-active": dashboardActiveIcon,
    contact: contactIcon,
    "contact-active": contactActiveIcon,
    anonymous: anonymousIcon,
    "anonymous-active": anonymousActiveIcon,
  };

  const navItems = [
    {
      path: "/settings",
      icon: "setting",
      activeIcon: "setting-active",
      iconType: "setting",
    },
    {
      path: "/",
      icon: "dashboard",
      activeIcon: "dashboard-active",
      iconType: "dashboard",
    },
    {
      path: "/contacts",
      icon: "contact",
      activeIcon: "contact-active",
      iconType: "contact",
    },
    {
      path: "/anonymous",
      icon: "anonymous",
      activeIcon: "anonymous-active",
      iconType: "anonymous",
    },
  ];

  return (
    <StyledNavContainer>
      <StyledNav>
        {navItems.map((item) => {
          const isActive = isNavItemActive(item.path, location.pathname);
          const iconName = isActive ? item.activeIcon : item.icon;

          return (
            <NavItem
              key={item.path}
              onClick={() => handleNavItemClick(item.path)}
              $isActive={isActive}
              $iconType={item.iconType}
            >
              <img
                src={iconMap[iconName as keyof typeof iconMap]}
                alt={item.path === "/" ? "Dashboard" : item.path.slice(1)}
              />
            </NavItem>
          );
        })}
      </StyledNav>
    </StyledNavContainer>
  );
};

export default Navigation;
